/**
 * Employee Asset Assignments — issue + track returns of PPE / uniforms /
 * tools / ID cards / laptops / mobiles / vehicles.
 *
 * Route: /hr/employee-assets
 *
 * On separation, any row with return_status != RETURNED feeds
 * asset_damage_recovery in F&F. LOST defaults damage = issue_value.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button, Card, DatePicker, Drawer, Form, Input, InputNumber, Modal, Select,
  Space, Table, Tag, Typography, message,
} from 'antd'
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'

import {
  employeeAssetsApi, ASSET_TYPES, ASSET_RETURN_STATUSES, ASSET_TYPE_LABEL,
  RETURN_STATUS_COLOR, type EmployeeAssetAssignment, type AssetType,
  type AssetReturnStatus,
} from '@/features/hr/employeeAssetsApi'
import { employeesApi } from '@/features/hr/api'

const { Title, Text } = Typography

export default function EmployeeAssetsPage() {
  const qc = useQueryClient()
  const [empFilter, setEmpFilter] = useState<string | undefined>()
  const [typeFilter, setTypeFilter] = useState<AssetType | undefined>()
  const [outstandingOnly, setOutstandingOnly] = useState(false)
  const [issueOpen, setIssueOpen] = useState(false)
  const [returnRow, setReturnRow] = useState<EmployeeAssetAssignment | null>(null)
  const [issueForm] = Form.useForm()
  const [returnForm] = Form.useForm()

  const empListQ = useQuery({
    queryKey: ['employees-for-assets'],
    queryFn: () => employeesApi.list({ page: 1, size: 1000 }),
  })
  const empOptions = (empListQ.data?.items ?? []).map((e) => ({
    label: `${e.emp_code} — ${e.emp_name}`, value: e.id,
  }))

  const listQ = useQuery({
    queryKey: ['employee-assets', empFilter, typeFilter, outstandingOnly],
    queryFn: () => employeeAssetsApi.list({
      employee_id: empFilter,
      asset_type: typeFilter,
      outstanding_only: outstandingOnly,
    }),
  })

  const issueMut = useMutation({
    mutationFn: employeeAssetsApi.issue,
    onSuccess: () => {
      message.success('Asset issued')
      setIssueOpen(false)
      issueForm.resetFields()
      qc.invalidateQueries({ queryKey: ['employee-assets'] })
    },
    onError: () => message.error('Failed to issue asset'),
  })

  const returnMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof employeeAssetsApi.recordReturn>[1] }) =>
      employeeAssetsApi.recordReturn(id, body),
    onSuccess: () => {
      message.success('Return recorded')
      setReturnRow(null)
      returnForm.resetFields()
      qc.invalidateQueries({ queryKey: ['employee-assets'] })
    },
    onError: () => message.error('Failed to record return'),
  })

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 4 }}>Employee Assets</Title>
      <Text type="secondary">
        PPE / uniforms / tools / ID cards / laptops / mobiles / vehicles issued to employees.
        Outstanding items feed F&F asset damage recovery on separation.
      </Text>

      <Card size="small" style={{ marginTop: 16, marginBottom: 16 }}>
        <Space wrap>
          <Text>Employee:</Text>
          <Select
            allowClear
            showSearch
            style={{ width: 320 }}
            value={empFilter}
            onChange={setEmpFilter}
            placeholder="All employees"
            options={empOptions}
            filterOption={(input, option) =>
              (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
          <Text>Type:</Text>
          <Select
            allowClear
            style={{ width: 160 }}
            value={typeFilter}
            onChange={setTypeFilter}
            placeholder="All types"
            options={ASSET_TYPES.map((t) => ({ value: t, label: ASSET_TYPE_LABEL[t] }))}
          />
          <Select
            style={{ width: 200 }}
            value={outstandingOnly ? 'OUTSTANDING' : 'ALL'}
            onChange={(v) => setOutstandingOnly(v === 'OUTSTANDING')}
            options={[
              { value: 'ALL', label: 'All statuses' },
              { value: 'OUTSTANDING', label: 'Outstanding only (not returned)' },
            ]}
          />
          <Button icon={<ReloadOutlined />} onClick={() => listQ.refetch()}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setIssueOpen(true)}>
            Issue Asset
          </Button>
        </Space>
      </Card>

      <Table<EmployeeAssetAssignment>
        size="small"
        rowKey="id"
        loading={listQ.isLoading}
        dataSource={listQ.data ?? []}
        pagination={{ pageSize: 50, showSizeChanger: true }}
        columns={[
          {
            title: 'Employee',
            key: 'emp',
            render: (_, r) => (
              <div>
                <div>{r.employee_code}</div>
                <Text type="secondary" style={{ fontSize: 12 }}>{r.emp_name}</Text>
              </div>
            ),
          },
          {
            title: 'Asset',
            key: 'asset',
            render: (_, r) => (
              <div>
                <Tag>{ASSET_TYPE_LABEL[r.asset_type]}</Tag>
                <span>{r.asset_description}</span>
                {r.asset_code && (
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                    Code: {r.asset_code}
                  </Text>
                )}
              </div>
            ),
          },
          { title: 'Issued', dataIndex: 'issued_on', key: 'issued_on' },
          {
            title: 'Issue Value',
            dataIndex: 'issue_value',
            key: 'issue_value',
            align: 'right',
            render: (v: number | null) => v != null ? `₹${v.toLocaleString('en-IN')}` : '-',
          },
          {
            title: 'Status',
            dataIndex: 'return_status',
            key: 'return_status',
            render: (s: AssetReturnStatus) => (
              <Tag color={RETURN_STATUS_COLOR[s]}>{s.replace('_', ' ')}</Tag>
            ),
          },
          { title: 'Returned', dataIndex: 'returned_on', key: 'returned_on' },
          {
            title: 'Recovery',
            dataIndex: 'damage_recovery_amount',
            key: 'damage_recovery_amount',
            align: 'right',
            render: (v: number | null) => v != null && v > 0
              ? <Text type="danger">₹{v.toLocaleString('en-IN')}</Text>
              : '-',
          },
          {
            title: 'Action',
            key: 'action',
            render: (_, r) => r.return_status === 'ISSUED' ? (
              <Button size="small" onClick={() => {
                setReturnRow(r)
                returnForm.setFieldsValue({
                  return_status: 'RETURNED',
                  returned_on: dayjs(),
                  damage_recovery_amount: 0,
                })
              }}>
                Record Return
              </Button>
            ) : null,
          },
        ]}
      />

      {/* Issue Drawer */}
      <Drawer
        title="Issue Asset"
        open={issueOpen}
        onClose={() => setIssueOpen(false)}
        width={480}
        destroyOnClose
      >
        <Form
          form={issueForm}
          layout="vertical"
          onFinish={(v) => issueMut.mutate({
            ...v,
            issued_on: (v.issued_on as dayjs.Dayjs).format('YYYY-MM-DD'),
          })}
          initialValues={{ issued_on: dayjs(), asset_type: 'PPE' }}
        >
          <Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}>
            <Select
              showSearch
              options={empOptions}
              filterOption={(input, option) =>
                (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
              }
              placeholder="Pick employee"
            />
          </Form.Item>
          <Form.Item name="asset_type" label="Asset Type" rules={[{ required: true }]}>
            <Select options={ASSET_TYPES.map((t) => ({ value: t, label: ASSET_TYPE_LABEL[t] }))} />
          </Form.Item>
          <Form.Item name="asset_description" label="Description" rules={[{ required: true }]}>
            <Input placeholder="e.g. Safety helmet, Laptop Lenovo X1" />
          </Form.Item>
          <Form.Item name="asset_code" label="Asset Code (optional)">
            <Input placeholder="Serial number / asset tag" />
          </Form.Item>
          <Form.Item name="issued_on" label="Issued On" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="issue_value" label="Issue Value (₹)" tooltip="Used as default recovery for LOST assets">
            <InputNumber min={0} step={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={issueMut.isPending}>Issue</Button>
              <Button onClick={() => setIssueOpen(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>

      {/* Return Modal */}
      <Modal
        title="Record Return"
        open={!!returnRow}
        onCancel={() => setReturnRow(null)}
        onOk={() => returnForm.submit()}
        confirmLoading={returnMut.isPending}
        destroyOnClose
      >
        {returnRow && (
          <Form
            form={returnForm}
            layout="vertical"
            onFinish={(v) => returnMut.mutate({
              id: returnRow.id,
              body: {
                return_status: v.return_status,
                returned_on: v.returned_on ? (v.returned_on as dayjs.Dayjs).format('YYYY-MM-DD') : undefined,
                damage_recovery_amount: v.damage_recovery_amount,
                remarks: v.remarks,
              },
            })}
          >
            <Text type="secondary">
              {returnRow.employee_code} — {returnRow.asset_description}
            </Text>
            <div style={{ height: 12 }} />
            <Form.Item name="return_status" label="Status" rules={[{ required: true }]}>
              <Select
                options={ASSET_RETURN_STATUSES
                  .filter((s) => s !== 'ISSUED')
                  .map((s) => ({ value: s, label: s.replace('_', ' ') }))}
              />
            </Form.Item>
            <Form.Item name="returned_on" label="Returned On">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="damage_recovery_amount"
              label="Damage Recovery (₹)"
              tooltip="Auto-defaults to issue value for LOST assets"
            >
              <InputNumber min={0} step={100} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="remarks" label="Remarks">
              <Input.TextArea rows={2} />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  )
}
